






































































































































































import { Component, Mixins } from "vue-property-decorator";
import Search from "@/components/elements/utility/Search.vue";
import DataTable from "@/components/elements/utility/Table.vue";
import StickyHScroll from "@/components/elements/utility/StickyHScroll.vue";
import { Order } from "@/models/order/order";
import { NetSuiteQuoteStatus } from "@/models/netsuite/helpers";
import { NetSuiteCustomer } from "@/models/netsuite/netsuite";
import ConfirmNewQuote from "@/components/elements/modal/ConfirmNewQuote.vue";
import { OrderMixin } from "@/mixins/orders/mixin";
import { CustomerMixin } from "@/mixins/customers/mixin";
import { Mobile } from "@/mixins/mobile";
import ConfirmClearAll from "@/components/elements/modal/ConfirmClearAll.vue";
import ConfirmDelete from "@/components/elements/modal/ConfirmDelete.vue";
import { GenericError } from "@/services/error_service";
import { NotificationService } from "@/services/notification_service";

@Component({
  components: {
    Search,
    DataTable,
    StickyHScroll,
    ConfirmNewQuote,
    ConfirmClearAll,
    ConfirmDelete
  }
})
export default class CustomerOrders extends Mixins(
  Mobile,
  OrderMixin,
  CustomerMixin
) {
  protected customer: NetSuiteCustomer = {} as NetSuiteCustomer;
  protected id = "";
  protected loader: any = null;
  protected showModalNew = false;

  // Getter (not a data field): field initializers run before mixin data is
  // merged onto the instance, so this.draftsTabKey would be undefined there.
  // No Legacy tab: legacy (Mongo) customers carry no NetSuite id, so legacy
  // quotes cannot be tied to the NetSuite customer this page is scoped to.
  protected get tabs(): Array<{ key: string; label: string }> {
    return [
      { key: this.draftsTabKey, label: "Drafts" },
      { key: NetSuiteQuoteStatus.Open, label: "Open" },
      { key: NetSuiteQuoteStatus.Processed, label: "Processed" },
      { key: NetSuiteQuoteStatus.Expired, label: "Expired" }
    ];
  }

  // The Processed tab is the only one with a Sales Order # column, so it is the
  // only tab whose search matches on it (see OrderMixin.filteredOrders).
  protected get searchPlaceholder(): string {
    return this.activeTab === NetSuiteQuoteStatus.Processed
      ? "Search by Quote Name, Quote #, or Sales Order #"
      : "Search by Quote Name or Quote #";
  }

  async created() {
    this.showLoader("Obtaining Quotes...");
    if (this.$route.params.id) {
      this.id = this.$route.params.id as string;
      // The customer lookup only feeds the loader label and header; both quote
      // fetches key off the route id, so none of these need to wait on another.
      await Promise.all([
        this.prepareCustomer(),
        this.prepareNetSuiteQuotes(),
        this.prepareDrafts()
      ]);
      this.orders = this.convertDraftsToOrders(this.drafts).concat(
        this.netSuiteQuotes
      );
      if (this.tabCounts[this.draftsTabKey] > 0) {
        this.activeTab = this.draftsTabKey;
      } else if (this.tabCounts[this.activeTab] === 0) {
        const firstNonEmpty = this.tabs.find(t => this.tabCounts[t.key] > 0);
        if (firstNonEmpty) {
          this.activeTab = firstNonEmpty.key;
        }
      }
      this.closeLoader();
    } else {
      this.closeLoader();
      this.$router.replace("/customers");
    }
  }

  protected showDeleteModal = false;
  protected draftPendingDelete: Order | null = null;

  protected askDeleteDraft(order: Order): void {
    this.draftPendingDelete = order;
    this.showDeleteModal = true;
  }

  protected closeDeleteDraft(): void {
    this.draftPendingDelete = null;
    this.showDeleteModal = false;
  }

  protected async confirmDeleteDraft(): Promise<void> {
    const order = this.draftPendingDelete;
    this.closeDeleteDraft();
    if (!order?.draft_id) return;
    try {
      await this.deleteDraftAction(order.draft_id);
      this.drafts = this.drafts.filter(d => d.id !== order.draft_id);
      this.orders = this.orders.filter(o => o.draft_id !== order.draft_id);
      NotificationService.show("Draft deleted.", "success");
    } catch (err) {
      GenericError.popup(err.message);
    }
  }

  protected async prepareCustomer(): Promise<void> {
    this.customer = await this.netSuiteService.getNetSuiteCustomerById(this.id);
    this.showLoader(`Obtaining ${this.customer.company_name} Quotes...`);
  }

  protected async prepareDrafts(): Promise<void> {
    try {
      // Drafts are private to the current user; scope them to this customer.
      const myDrafts = await this.quoteDraftService.listQuotes();
      this.drafts = myDrafts.filter(
        d => d.netsuite_customer_id === Number(this.id)
      );
    } catch (err) {
      console.warn("Quote drafts unavailable:", err);
      this.drafts = [];
    }
  }

  protected async prepareNetSuiteQuotes(): Promise<void> {
    const customerNetSuiteQuotes = await this.netSuiteService.getCustomerNetSuiteQuotes(
      parseInt(this.id)
    );
    // // ToDo: FNSI-101 - get the quote line items for each quote in the array.
    // const netSuiteQuotesWithLineItems = await this.getNetSuiteQuoteLineItems(
    //   customerNetSuiteQuotes
    // );
    // console.log(
    //   "decide what to do with netSuiteQuotesWithLineItems",
    //   netSuiteQuotesWithLineItems
    // );
    // this.convertNetSuiteQuotes(netSuiteQuotesWithLineItems);
    this.convertNetSuiteQuotes(customerNetSuiteQuotes);
  }

  protected closeModal(): void {
    this.showModalNew = false;
  }

  protected async clearOrder(): Promise<void> {
    await this.resetOrder();
    await this.reassignQuote();
  }

  protected toggleNewQuoteModal(): void {
    // getOrderItems can return undefined, so guard before reading .length —
    // otherwise the click handler throws and the link silently does nothing.
    if (this.items && this.items.length > 0) {
      // only toggling warning modal if there is an item associated with the order
      this.showModalNew = true;
    } else {
      this.reassignQuote();
    }
  }

  protected async reassignQuote() {
    // prepareCustomer() may have left this.customer empty (getNetSuiteCustomerById
    // swallows errors and returns {}). Without this guard, customer_id.toString()
    // throws and the promise rejects unhandled — the "Start a quote" link appears
    // to do nothing. Surface the problem instead of failing silently.
    if (!this.customer?.customer_id) {
      GenericError.popup(
        "Customer details could not be loaded. Please refresh and try again."
      );
      return;
    }
    try {
      await this.saveCustomerToStore(
        this.customer.customer_id.toString(),
        this.customer.company_name,
        1 + (this.customer.price_level_discount ?? 0),
        this.customer.customer_id,
        this.customer.payment_terms_id || undefined,
        this.customer.price_level_id,
        this.customer.price_level_discount ?? undefined,
        null
      );
    } catch (err) {
      GenericError.popup(err.message);
      return;
    }
    this.close();
    await this.$router.push("/");
  }

  protected showLoader(message = "Loading..."): void {
    if (this.loader) {
      this.loader.hide();
    }
    this.loader = this.$loading.show(
      { opacity: 1 },
      {
        before: this.$createElement("h1", message)
      }
    );
  }

  protected closeLoader(): void {
    setTimeout(() => {
      this.loader.hide();
    }, 300);
  }
}
